import {
  Css,
  GridDataRow,
  Icon,
  LoadingSkeleton,
  ScrollableContent,
  Tooltip,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
  useToast,
} from "@homebound/beam";
import xor from "lodash/xor";
import { useCallback, useMemo, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  createBidPackagesDetailUrl,
  createDevelopmentContractOverviewUrl,
  createProductOfferingsUrl,
  createProjectBidContractOverviewUrl,
} from "src/RouteUrls";
import { priceCell } from "src/components";
import { ArchivedTag } from "src/components/ArchivedTag";
import { SideNavProps } from "src/components/layout/SideNav";
import { SideNavLayout } from "src/components/layout/SideNavLayout";
import { AggregateVersionStatus, ItivOrderField, Maybe, Order } from "src/generated/graphqlTypes";
import { EmptyState } from "src/routes/components/EmptyState";
import {
  ProductOfferingScopeMetaQuery,
  ProductOfferingScopeMeta_BidPackageFragment,
  ProductOfferingScopePage_GroupTotalsFragment,
  ProductOfferingScopePage_PreviousTlivFragment,
  ProductOfferingScopePage_TliFragment,
  useProductOfferingScopeMetaQuery,
  useProductOfferingScopePageQuery,
} from "src/routes/developments/product-offerings/ProductOfferingScopePage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import { TruncatedNameListCell } from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/TruncatedNameListCell";
import { ProductOfferingScopeParams } from "src/routes/routesDef";
import { foldEnum, pluralize, queryResult, roundToDigits } from "src/utils";
import { maybeAddIdColumn } from "src/utils/idColumn";
import { subFilterByOrder } from "src/utils/itemTemplateItem";
import { StringParam, useQueryParams } from "use-query-params";
import { EmptyPlanAndOptionImage } from "../plan-and-options/components/EmptyStateSvgs";
import { ProductOfferingContextProvider, useProductOfferingStore } from "./ProductOfferingScopeContext";
import { ProductOfferingScopePageSideBar } from "./ProductOfferingScopePageSideBar";
import { CostSourceCell } from "./components/CostSourceCell";
import { DropCodeCell } from "./components/DropCodeCell";
import { ProductOfferingScopeTable } from "./components/ProductOfferingScopeTable";
import { ProductOfferingScopeTableActions } from "./components/ProductOfferingScopeTableActions";

// TODO: SC-52964 Get cost status enum from the BE
enum CostStatus {
  GetEstimate = "Get Estimate",
  InternalEstimate = "Internal Estimate",
  Awarded = "Awarded",
}

// Most of this is copied from the
// PlanPackageTakeoffTable component - src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/PlanPackageTakeoffTable.tsx
// Since need to support the same views and functionality of that table, for offering scope (excluding edit functionality)
export function ProductOfferingScopePage() {
  const { productOfferingId, rpavId } = useParams<ProductOfferingScopeParams>();
  const query = useProductOfferingScopeMetaQuery({ variables: { id: productOfferingId, rpavId } });
  return (
    <>
      {queryResult(query, ({ productOffering }) => (
        <ProductOfferingContextProvider productOffering={productOffering}>
          <ProductOfferingScopeView productOffering={productOffering} rpavId={rpavId} />
        </ProductOfferingContextProvider>
      ))}
    </>
  );
}

type ProductOfferingScopeViewProps = ProductOfferingScopeMetaQuery & { rpavId: string };

function ProductOfferingScopeView(props: ProductOfferingScopeViewProps) {
  const [{ devId: developmentId, projectId }] = useQueryParams({
    devId: StringParam,
    projectId: StringParam,
  });
  const { productOffering, rpavId } = props;
  const history = useHistory();
  const { showToast } = useToast();
  // Get the reactive store states
  const filter = useProductOfferingStore((state) => state.filter);
  const itemTableGroupBy = useProductOfferingStore((state) => state.itemTableGroupBy);
  const itemTableSortBy = useProductOfferingStore((state) => state.itemTableSortBy);

  const sideNavProps: SideNavProps = {
    sideNavConfig: [],
    top: <ProductOfferingScopePageSideBar {...props} />,
    navWidth: 265,
  };

  const { data, loading, previousData, fetchMore } = useProductOfferingScopePageQuery({
    variables: {
      filter: { readyPlan: [productOffering.id], ...filter },
      rpavId,
      first: 25,
      order: [
        { field: itemTableGroupBy, direction: Order.Asc },
        // we would like the drops to be ordered at the top of the table when order by cost code or cost division
        ...(itemTableGroupBy === ItivOrderField.CostCode || itemTableGroupBy === ItivOrderField.CostDivision
          ? [
              {
                field: ItivOrderField.Drop,
                direction: Order.Asc,
              },
            ]
          : []),
        { field: itemTableSortBy, direction: Order.Asc },
      ],
    },
    fetchPolicy: "cache-and-network",
  });

  const failedGroupIds = useRef<string[]>([]);
  // Lazy load tlis as the user scrolls to the row
  // This allows us to support large datasets without loading all the data upfront
  const fetchMoreGroupItems = useCallback(
    async (groupTotals: ProductOfferingScopePage_GroupTotalsFragment) => {
      // Early return if the group has failed to load
      if (failedGroupIds.current.includes(groupTotals.groupId)) return;
      try {
        await fetchMore({
          variables: {
            first: null,
            subFilter: subFilterByOrder(itemTableGroupBy, groupTotals.groupId),
          },
        });
      } catch (e) {
        console.error(e);
        failedGroupIds.current.push(groupTotals.groupId);
        showToast({ type: "error", message: `Failed to fetch items for group ${groupTotals.displayName}` });
      }
    },
    [fetchMore, showToast, itemTableGroupBy, failedGroupIds],
  );

  const { items = [], groupTotals = [] } = data?.takeoffLineItemsPage ?? previousData?.takeoffLineItemsPage ?? {};
  const bidPackages = useMemo(() => productOffering.bidPackages, [productOffering.bidPackages]);
  // Highlight changes only when viewing a draft or previous version
  const highlightChanges =
    productOffering.version.status.code === AggregateVersionStatus.Draft ||
    productOffering.version.status.code === AggregateVersionStatus.Archived;

  const columns = useMemo(
    () => createColumns(fetchMoreGroupItems, bidPackages, developmentId, projectId, highlightChanges),
    [fetchMoreGroupItems, bidPackages, developmentId, projectId, highlightChanges],
  );
  const rows = useMemo(() => createRows(items, groupTotals, itemTableGroupBy), [items, groupTotals, itemTableGroupBy]);

  return (
    <SideNavLayout sideNavProps={sideNavProps}>
      <PageHeader
        title={productOffering.name}
        breadcrumb={[{ label: "Product Offerings", href: createProductOfferingsUrl(developmentId, projectId) }]}
      />
      <ProductOfferingScopeTableActions />
      <ScrollableContent virtualized>
        {loading ? (
          <>
            <LoadingSkeleton rows={1} columns={1} />
            <LoadingSkeleton rows={5} columns={5} />
          </>
        ) : rows.length === 1 && Object.keys(filter).length === 0 ? (
          <EmptyState message="There is no scope for this offering yet" svg={<EmptyPlanAndOptionImage />} />
        ) : (
          <ProductOfferingScopeTable columns={columns} rows={rows} />
        )}
      </ScrollableContent>
    </SideNavLayout>
  );
}

function createRows(
  tlis: ProductOfferingScopePage_TliFragment[],
  groupTotals: ProductOfferingScopePage_GroupTotalsFragment[],
  itemTableGroupBy: ItivOrderField,
): GridDataRow<ScopePageRows>[] {
  return [
    { kind: "header", id: "header", data: undefined },
    ...groupTotals
      .filter((gt) => gt.groupId !== "queryTotal")
      .map((gt) => {
        const itemsInGroup = tlis.filter((rpo) => gt.tliIdsInGroup.includes(rpo.id)) ?? [];
        const children: Array<GridDataRow<ScopePageRows>> = [];
        // Group the tlis by drop if the groupBy is cost code or cost division
        if (itemTableGroupBy === ItivOrderField.CostCode || itemTableGroupBy === ItivOrderField.CostDivision) {
          // This ordering is reliant upon the conditional ordering in the query
          const noDropKey = "no-drop";
          const itemsGroupedByDrop = itemsInGroup.groupBy((itiv) => itiv.drop?.id ?? noDropKey);
          Object.keys(itemsGroupedByDrop).forEach((key) => {
            // This if is to handle a rendering bug where the no drop children are not rendered
            if (key === noDropKey) {
              // add no drop children to the children array
              if (itemsGroupedByDrop[noDropKey]) {
                itemsGroupedByDrop[noDropKey].forEach((itiv) => {
                  children.push({
                    kind: "tli" as const,
                    id: itiv.id,
                    data: itiv,
                  });
                });
              }
              return;
            }
            const dropTlis = itemsGroupedByDrop[key];
            // Add the drop children to the children array
            children.push({
              kind: "drop" as const,
              id: dropTlis[0].drop!.id,
              data: {
                dropCode: dropTlis[0].drop!.code,
                name: dropTlis[0].drop!.name,
                numberOfItis: dropTlis.length,
              },
              children: dropTlis.map((itiv) => ({
                kind: "tli" as const,
                id: itiv.id,
                data: itiv,
              })),
            });
          });
        } else {
          // If the groupBy is not cost code or cost division, render the tlis as is
          itemsInGroup.forEach((itiv) => {
            children.push({
              kind: "tli" as const,
              id: itiv.id,
              data: itiv,
            });
          });
        }

        const loadedItems = itemsInGroup.length;
        const initCollapsed = loadedItems === 0;
        const partiallyLoaded = loadedItems !== gt.tlisInGroup;
        if (partiallyLoaded) {
          // Set the row kind to `loading` if the tlis are partially loaded
          children.push({ kind: "loading" as const, id: `loading-${gt.groupId}`, data: gt });
          if (loadedItems === 0)
            // If no tlis are loaded, set the groupBy row id to `not-loaded`
            return {
              kind: "groupBy" as const,
              initCollapsed,
              id: gt.groupId + "not-loaded",
              data: gt,
              children,
            };
        }
        return {
          kind: "groupBy" as const,
          id: gt.groupId,
          data: gt,
          children,
        };
      }),
  ];
}

export enum Column {
  ItemCode,
  Name,
  MaterialCode,
  GenericIcon,
  Quantity,
  UnitOfMeasure,
  Location,
  Option,
  TaskAllocation,
  UnitCost,
  CostSource,
  TotalCost,
}

function createColumns(
  fetchMoreGroupItems: (groupTotals: ProductOfferingScopePage_GroupTotalsFragment) => Promise<void>,
  bidPackages: ProductOfferingScopeMeta_BidPackageFragment[],
  developmentId: Maybe<string>,
  projectId: Maybe<string>,
  highlightChanges: boolean,
) {
  return [
    collapseColumn<ScopePageRows>({
      loading: emptyCell,
      sticky: "left",
      tli: (row) => ({
        content: emptyCell.content,
        css: highlightChanges ? applyStyling(row) : undefined,
      }),
    }),
    ...maybeAddIdColumn(
      column<ScopePageRows>({
        header: "Id",
        groupBy: emptyCell,
        drop: emptyCell,
        tli: (row) => row.id,
        loading: emptyCell,
        w: "100px",
      }),
    ),
    column<ScopePageRows>({
      id: `${Column.ItemCode}`,
      header: "Code",
      groupBy: (row) => ({
        colspan: itemColumnGroupByColspan,
        content: () => (
          <span css={Css.xl.w100.$}>
            <span css={Css.dib.truncate.w50.$}>
              {row.displayName.length > 55 ? (
                <Tooltip
                  title={
                    <ul css={Css.pl2.$}>
                      {row.displayName.split(",").map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  }
                >
                  {row.displayName}
                </Tooltip>
              ) : (
                row.displayName
              )}
            </span>
            <span css={Css.gray700.sm.ml3.$}>
              {`${row.materialsInGroup} ${pluralize(row.materialsInGroup, "material")}`}
            </span>
            <span css={Css.gray700.sm.ml3.$}>{`${row.tasksInGroup} ${pluralize(row.tasksInGroup, "task")}`}</span>
          </span>
        ),
        alignment: "left",
        css: Css.maxw("66.5vw").$,
      }),
      tli: (row) => ({
        content: <DropCodeCell scope={row} />,
        css: highlightChanges ? applyStyling(row, "drop") : undefined,
      }),
      loading: (row) => ({
        content: () => {
          fetchMoreGroupItems(row).catch(console.error);
          return (
            <>
              Loading...
              <Icon icon="refresh" />
            </>
          );
        },
      }),
      mw: "100px",
      sticky: "left",
      drop: (row) => row.dropCode,
    }),
    column<ScopePageRows>({
      id: `${Column.Name}`,
      header: "Name",
      groupBy: emptyCell,
      tli: (row) => ({
        content: row.name,
        css: highlightChanges ? applyStyling(row, "materialVariant") : undefined,
      }),
      loading: emptyCell,
      mw: "205px",
      sticky: "left",
      drop: (row) => ({
        content: row.name,
        css: Css.xsSb.$,
      }),
    }),
    column<ScopePageRows>({
      id: `${Column.MaterialCode}`,
      header: "Material Code",
      groupBy: emptyCell,
      tli: (row) => ({
        content: row.materialVariant?.code,
        css: highlightChanges ? applyStyling(row, "materialVariant") : undefined,
      }),
      loading: emptyCell,
      mw: "120px",
      sticky: "left",
      drop: emptyCell,
    }),
    numericColumn<ScopePageRows>({
      id: `${Column.Quantity}`,
      header: "QTY",
      groupBy: () => emptyCell,
      tli: (row) => ({
        content: row.unitOfMeasure?.useQuantity ? (row.quantity ?? "") : "N/A",
        css: highlightChanges ? applyStyling(row, "quantity") : undefined,
      }),
      loading: emptyCell,
      align: "right",
      mw: "80px",
      drop: (row) => row.numberOfItis + " " + pluralize(row.numberOfItis, "item"),
    }),
    column<ScopePageRows>({
      id: `${Column.UnitOfMeasure}`,
      header: "UoM",
      groupBy: () => emptyCell,
      tli: (row) => ({
        content: row.unitOfMeasure?.abbreviation,
        css: highlightChanges ? applyStyling(row, "unitOfMeasure") : undefined,
      }),
      loading: emptyCell,
      mw: "80px",
      drop: emptyCell,
    }),
    column<ScopePageRows>({
      id: `${Column.Location}`,
      header: "Location",
      groupBy: () => emptyCell,
      tli: (row) => ({
        content: row.location?.displayLocationPath,
        css: highlightChanges ? applyStyling(row, "location") : undefined,
      }),
      loading: emptyCell,
      mw: "140px",
      drop: emptyCell,
    }),
    column<ScopePageRows>({
      id: `${Column.Option}`,
      header: "Option",
      groupBy: () => emptyCell,
      tli: (row) => {
        const optionsCodes = row.options.map((rpo) => (
          <ArchivedTag key={rpo.id} active={rpo.active}>
            {rpo.code}
          </ArchivedTag>
        ));
        return {
          content: <TruncatedNameListCell nameList={optionsCodes} />,
          css: highlightChanges ? applyStyling(row, "options") : undefined,
        };
      },
      loading: emptyCell,
      mw: "140px",
      drop: emptyCell,
    }),
    column<ScopePageRows>({
      id: `${Column.TaskAllocation}`,
      header: "Task Allocation",
      groupBy: () => emptyCell,
      tli: (row) => ({
        content: row.task?.name,
        css: highlightChanges ? applyStyling(row, "task") : undefined,
      }),
      loading: emptyCell,
      mw: "200px",
      drop: emptyCell,
    }),
    column<ScopePageRows>({
      id: `${Column.UnitCost}`,
      header: "Unit Cost",
      groupBy: () => emptyCell,
      tli: (row) => ({
        ...priceCell({
          alignment: "left",
          valueInCents: roundToDigits(row.totalCostInCents / (row.quantity || 1), 1),
          maxDecimalPlaces: 3,
          minDecimalPlaces: 2,
        }),
        css: highlightChanges ? applyStyling(row) : undefined,
      }),
      loading: emptyCell,
      mw: "200px",
      drop: emptyCell,
    }),
    column<ScopePageRows>({
      id: `${Column.CostSource}`,
      header: "Cost Source",
      groupBy: () => emptyCell,
      tli: (row) => {
        const maybeBidPackage = bidPackages.find((bp) => bp.costCodes.some((cc) => cc.id === row.item.costCode.id));
        let url;
        if (developmentId) {
          url = !row.preferredBidContractLineItem
            ? // Route to the bidders tab if no preferredBCLI
              maybeBidPackage && createBidPackagesDetailUrl(maybeBidPackage.bidPackageGroup!.id, maybeBidPackage.id)
            : // Else route to the contract page (internal estimate or awarded should be the same contract route)
              createDevelopmentContractOverviewUrl(developmentId, row.preferredBidContractLineItem.revision.id);
        } else if (projectId) {
          url = !row.preferredBidContractLineItem
            ? // Route to the bidders tab if no preferredBCLI
              maybeBidPackage && createBidPackagesDetailUrl(maybeBidPackage.bidPackageGroup!.id, maybeBidPackage.id)
            : // Else route to the contract page (internal estimate or awarded should be the same contract route)
              createProjectBidContractOverviewUrl(projectId, row.preferredBidContractLineItem.revision.id);
        }

        // TODO: SC-52964 Get cost status directly from the BE
        const label = !row.preferredBidContractLineItem
          ? "Get Estimate"
          : row.preferredBidContractLineItem.revision.bidContract.isInternalEstimate
            ? "Internal Estimate"
            : "Awarded";

        return {
          content: (
            <CostSourceCell
              url={url}
              label={label}
              icon={foldEnum(label, {
                [CostStatus.GetEstimate]: "dollar",
                [CostStatus.InternalEstimate]: "document",
                [CostStatus.Awarded]: "check",
              })}
            />
          ),
          css: highlightChanges ? applyStyling(row) : undefined,
        };
      },
      loading: emptyCell,
      mw: "250px",
      drop: emptyCell,
    }),
    column<ScopePageRows>({
      id: `${Column.TotalCost}`,
      header: "Total Cost",
      groupBy: () => emptyCell,
      tli: (row) => ({
        ...priceCell({ alignment: "left", valueInCents: row.totalCostInCents }),
        css: highlightChanges ? applyStyling(row) : undefined,
      }),
      loading: emptyCell,
      mw: "200px",
      drop: emptyCell,
    }),
  ];
}

type Field = keyof Pick<
  ProductOfferingScopePage_PreviousTlivFragment,
  "options" | "quantity" | "unitOfMeasure" | "materialVariant" | "task" | "location" | "drop"
>;

function fieldHasChanged(row: ProductOfferingScopePage_TliFragment, field?: Field): boolean {
  if (!row.previous || !field) return false;

  if (field === "quantity") {
    return row.previous.quantity !== row.quantity;
  }

  if (field === "options") {
    return optionsHaveChanged(row);
  }

  const previous = row.previous[field]?.id;
  const current = row[field]?.id;

  return previous !== current;
}

function applyStyling(row: ProductOfferingScopePage_TliFragment, field?: Field) {
  if (row.changeType === AggregateMemberVersionChangeType.ADDED) {
    return Css.bgYellow50.$;
  }
  if (row.changeType === AggregateMemberVersionChangeType.REMOVED) {
    return Css.bgGray50.tdlt.gray500.$;
  }
  if (row.changeType === AggregateMemberVersionChangeType.UPDATED && fieldHasChanged(row, field)) {
    return Css.bgYellow50.$;
  }
}

function optionsHaveChanged(curr: ProductOfferingScopePage_TliFragment) {
  const prev = curr.previous;
  if (!prev || curr.changeType !== AggregateMemberVersionChangeType.UPDATED) return false;
  return xor(
    prev.options?.map((o) => o.id),
    curr.options?.map((o) => o.id),
  ).nonEmpty;
}

type HeaderRow = { kind: "header" };
type GroupByRow = { kind: "groupBy"; data: ProductOfferingScopePage_GroupTotalsFragment };
type ItemRow = { kind: "tli"; data: ProductOfferingScopePage_TliFragment; id: string };
type DropRow = { kind: "drop"; data: { dropCode: string; name: string; numberOfItis: number }; id: string };
// LoadingRow is a row that is used to indicate that the group is loading
// Since we are lazy loading the table items
type LoadingRow = { kind: "loading"; data: ProductOfferingScopePage_GroupTotalsFragment; id: string };
export type ScopePageRows = HeaderRow | GroupByRow | ItemRow | DropRow | LoadingRow;
export const itemColumnGroupByColspan = 8;
